import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { getMe, getValidAccessToken } from '@/services/authService';

interface AvatarContextType {
  selectedAvatar: string;
  setSelectedAvatar: (avatar: string) => void;
  getAvatarDisplay: (avatarUrl: string) => string;
  getAvatarEmoji: (avatarUrl: string) => string;
  refreshAvatar: () => Promise<void>;
}

const AvatarContext = createContext<AvatarContextType | undefined>(undefined);

// Default avatar URL fallback
const DEFAULT_AVATAR = 'https://api.dicebear.com/7.x/avataaars/svg?seed=default&backgroundColor=b6e3f4&clothesColor=262e33';

interface AvatarProviderProps {
  children: ReactNode;
}

export const AvatarProvider = ({ children }: AvatarProviderProps) => {
  const [selectedAvatar, setSelectedAvatarState] = useState<string>(DEFAULT_AVATAR);

  // Load avatar from backend and localStorage on mount
  useEffect(() => {
    const loadAvatar = async () => {
      try {
        // Ensure we have a valid access token before calling /api/auth/me
        const token = await getValidAccessToken();
        if (!token) {
          console.warn('⚠️ [Avatar] No access token available yet; skipping initial avatar fetch');
          return;
        }
        // Try to get avatar from backend first
        const profile = await getMe();
        if (profile.avatar_url) {
          setSelectedAvatarState(profile.avatar_url);
          // Update localStorage with backend value
          localStorage.setItem('userAvatar', profile.avatar_url);
          return;
        }
      } catch (error) {
        console.warn('⚠️ Failed to load avatar from backend:', error);
      }

      // Fallback to localStorage
      const savedAvatar = localStorage.getItem('userAvatar');
      if (savedAvatar) {
        setSelectedAvatarState(savedAvatar);
      }
    };

    loadAvatar();
  }, []);

  // Save avatar to localStorage when it changes
  const setSelectedAvatar = (avatar: string) => {
    setSelectedAvatarState(avatar);
    localStorage.setItem('userAvatar', avatar);
  };

  // Refresh avatar from backend
  const refreshAvatar = async () => {
    try {
      const token = await getValidAccessToken();
      if (!token) {
        console.warn('⚠️ [Avatar] No access token available for refresh; skipping');
        return;
      }
      const profile = await getMe();
      if (profile.avatar_url) {
        setSelectedAvatarState(profile.avatar_url);
        localStorage.setItem('userAvatar', profile.avatar_url);
        return;
      }
      // fallback: keep or use localStorage if present
      const savedAvatar = localStorage.getItem('userAvatar');
      if (savedAvatar) setSelectedAvatarState(savedAvatar);
    } catch (error) {
      console.warn('⚠️ Failed to refresh avatar from backend:', error);
    }
  };

  // Expose a safe global to refresh avatar after login/register flows
  useEffect(() => {
    (window as any).refreshAvatar = async () => {
      try {
        await refreshAvatar();
      } catch {}
    };
    return () => {
      try { delete (window as any).refreshAvatar; } catch {}
    };
  }, []);

  // Get display value for avatar URL (returns the URL itself or fallback)
  const getAvatarDisplay = (avatarUrl: string): string => {
    return avatarUrl || DEFAULT_AVATAR;
  };

  // Get emoji from avatar URL seed
  // Get emoji from avatar URL seed
  const getAvatarEmoji = (avatarUrl: string): string => {
    try {
      const url = new URL(avatarUrl);
      const seed = url.searchParams.get('seed');
      // The seed is the emoji itself in this avatar service
      return seed ? decodeURIComponent(seed) : '👤';
    } catch (e) {
      return '👤'; // Return a default emoji on parsing error
    }
  };

  const value = {
    selectedAvatar,
    setSelectedAvatar,
    getAvatarDisplay,
    getAvatarEmoji,
    refreshAvatar,
  };

  return (
    <AvatarContext.Provider value={value}>
      {children}
    </AvatarContext.Provider>
  );
};

export const useAvatar = (): AvatarContextType => {
  const context = useContext(AvatarContext);
  if (!context) {
    throw new Error('useAvatar must be used within an AvatarProvider');
  }
  return context;
};
